import { computed, onBeforeUnmount, reactive, ref } from 'vue';
import apiClient from '@/utils/axios.js';
import echo from '@/echo.js';
import { useAuthStore } from '@/stores/auth-store.js';
import { buildLocalManifest, searchLocalState } from '@/services/localStateSearch.js';

/**
 * Everything Ask Inventree does, minus how it looks.
 *
 * Extracted because the feature has two faces — the full page at /ask and the
 * floating messenger that follows you around — and they must never drift. A
 * second copy of the polling, the socket teardown or the chip formatting is a
 * second place for a bug to live.
 *
 * Each caller gets its own instance, so the page and the launcher hold separate
 * conversations rather than fighting over one.
 *
 * A conversation is a list of TURNS, not a single answer. The server groups
 * them by `conversation_id` and shows the earlier ones to the planner, which is
 * what makes "and last month?" mean anything — see docs/ask-inventree-plan.md.
 */
export function useAsk() {
    const auth = useAuthStore();

    const question = ref('');
    const busy = ref(false);
    const errorMessage = ref('');

    /** The conversation on screen: its id, and its turns oldest first. */
    const conversationId = ref(null);
    const turns = ref([]);

    /** Past conversations, one row each, titled by the question that started them. */
    const history = ref([]);
    const vocabulary = ref({ tables: [], metrics: [], reports: [], dictation: null, local_state: null });

    const maxLength = 500;

    let echoChannel = null;
    let pollTimer = null;
    let watchingId = null;

    /** The turn being worked on, which is always the last one. */
    const latest = computed(() => turns.value[turns.value.length - 1] ?? null);
    const pending = computed(() => Boolean(latest.value) && !isFinished(latest.value));
    const started = computed(() => turns.value.length > 0);

    function isFinished(turn) {
        return ['completed', 'failed'].includes(turn?.status);
    }

    /**
     * Suggestions built from this user's own vocabulary rather than a fixed
     * list, so they are never an invitation to ask something that will be
     * refused.
     */
    const suggestions = computed(() => {
        const out = [];
        const metrics = vocabulary.value.metrics.map((m) => m.metric);
        const tables = vocabulary.value.tables.map((t) => t.table);

        if (metrics.includes('revenue')) out.push('How much did we sell last month?');
        if (metrics.includes('product_revenue')) out.push('What sold best last week?');
        if (tables.includes('sales')) out.push('Show unpaid sales from this month');
        if (tables.includes('purchases')) out.push('Which purchase orders are still unpaid?');
        if (tables.includes('damaged_items')) out.push('What damage was reported this month?');
        if (tables.includes('customers')) out.push('Customers in Lahore with a credit limit');

        // Last, so it never crowds out a question about the business — but
        // offered, because nobody guesses that a question about their own
        // browser is one this box will take.
        if (vocabulary.value.local_state?.searches) out.push('What is cached on this device?');

        return out.slice(0, 4);
    });

    /**
     * What each of the browser's stores is called in plain language.
     *
     * "dexie" and "pinia" are the names of libraries, which is the right label
     * in a plan and the wrong one in an answer somebody reads.
     */
    function localStoreLabel(store) {
        return (
            {
                dexie: 'Offline cache',
                pinia: 'App state',
                local_storage: 'Local storage',
                cookies: 'Cookies',
                all: 'This browser',
            }[store] ?? 'This browser'
        );
    }

    /**
     * How one turn's plan reads in plain language.
     *
     * A function of the turn rather than a computed off "the" answer, because a
     * thread shows several at once and each has to explain itself — the chips
     * are the single most important trust affordance here, and a follow-up is
     * exactly where a carried-forward filter needs to be visible.
     */
    function chipsFor(turn) {
        const plan = turn?.plan;
        if (!plan) return [];

        if (plan.intent === 'list' && plan.list) {
            const out = [humanise(plan.list.table)];

            if (plan.list.search) out.push(`matching "${plan.list.search}"`);

            for (const f of plan.list.filters ?? []) {
                out.push(`${humanise(f.key)} ${describeValue(f.value)}`);
            }

            if (plan.list.sortField) out.push(`by ${humanise(plan.list.sortField)} ${plan.list.sortDirection}`);

            return out;
        }

        if (plan.intent === 'measure' && plan.measure) {
            const out = [humanise(plan.measure.metric), `${plan.measure.window.from} to ${plan.measure.window.to}`];
            for (const d of plan.measure.dimensions ?? []) out.push(`by ${humanise(d)}`);
            return out;
        }

        if (plan.intent === 'local' && plan.local) {
            const out = [localStoreLabel(plan.local.store)];
            if (plan.local.collection) out.push(plan.local.collection);
            if (plan.local.search) out.push(`matching "${plan.local.search}"`);
            return out;
        }

        return [];
    }

    /**
     * Carry out a local answer here, in the browser, and hang the result on the
     * turn.
     *
     * The server planned WHERE to look and never looked — Dexie, Pinia,
     * localStorage and the cookie jar exist on this machine and nowhere else,
     * and no value from them is ever sent anywhere. So every local answer is
     * executed on display, including one reopened from history a week later:
     * what a cache holds is a fact about now, and a stored copy of last
     * Tuesday's would be worse than no answer.
     */
    async function resolveLocal(turn) {
        if (!turn || turn.surface !== 'local' || turn.status !== 'completed' || turn.localBusy) return;

        turn.localBusy = true;
        turn.localError = '';

        try {
            turn.local = await searchLocalState(turn.result ?? {});
        } catch {
            turn.localError = 'This browser would not let that be read.';
        } finally {
            turn.localBusy = false;
        }
    }

    function resultOf(turn) {
        return turn?.result ?? {};
    }

    function describeValue(value) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            if (value.min !== undefined && value.max !== undefined) return `between ${value.min} and ${value.max}`;
            if (value.min !== undefined) return `at least ${value.min}`;
            if (value.max !== undefined) return `up to ${value.max}`;
        }
        if (Array.isArray(value)) return `is ${value.join(' or ')}`;
        return `is ${value}`;
    }

    function humanise(key) {
        return String(key ?? '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    function rowLabel(row) {
        return row.branch_name ?? row.variant_name ?? row.period ?? row.damage_type ?? '—';
    }

    function cell(row, col) {
        const v = row?.[col];
        if (v === null || v === undefined || v === '') return '—';
        return v;
    }

    function formatValue(value, unit) {
        const n = Number(value ?? 0);
        if (unit === 'count') return n.toLocaleString();
        return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    function shortDate(iso) {
        if (!iso) return '';
        return new Date(iso).toLocaleDateString();
    }

    // ---------------------------------------------------------------
    // Asking
    // ---------------------------------------------------------------

    function askThis(text) {
        question.value = text;
        return ask();
    }

    /**
     * Ask, continuing the conversation on screen.
     *
     * The earlier turns stay exactly where they are: a follow-up adds to the
     * thread rather than replacing it, because the whole reason to ask one is
     * that the answer above is still worth looking at.
     */
    async function ask() {
        const text = question.value.trim();

        // Not while the turn above is still being worked out. The server only
        // shows the planner turns that COMPLETED, so a follow-up fired into a
        // half-finished thread would be planned blind against the very answer
        // it is following up on — and the turn it overtook would be left
        // spinning with nothing watching it.
        if (text.length < 3 || busy.value || pending.value) return;

        busy.value = true;
        errorMessage.value = '';
        stopWatching();

        try {
            const { data } = await apiClient.post(
                'ask',
                { question: text, conversation_id: conversationId.value, local: await localManifest() },
                { offlinePolicy: 'mutation' },
            );

            conversationId.value = data.question.conversation_id ?? data.question.id;
            turns.value = [...turns.value, data.question];
            question.value = '';
            startWatching(data.question.id);
            loadHistory();
        } catch (error) {
            errorMessage.value =
                error.response?.data?.message ||
                (error.code === 'ERR_OFFLINE_WRITE' ? 'You need to be online to ask a question.' : 'That question could not be sent.');
        } finally {
            busy.value = false;
        }
    }

    /**
     * Belt and braces, exactly as imports, exports and extractions do it: the
     * socket is the fast path and the poll covers a dropped connection.
     */
    function startWatching(id) {
        watchingId = id;

        const userId = auth.user?.id;

        if (userId && window.Echo) {
            echoChannel = echo.private(`ask.${userId}`);
            echoChannel.listen('.AskProgress', (event) => {
                if (event?.progress?.id === watchingId) handleProgress(event.progress);
            });
        }

        pollTimer = setInterval(async () => {
            try {
                const { data } = await apiClient.get(`ask/${watchingId}`, { skipProgress: true });
                handleProgress(data.question);
            } catch {
                // Keep polling until it finishes or the view is left.
            }
        }, 2000);
    }

    function handleProgress(progress) {
        if (!progress || progress.id !== watchingId) return;

        // Replace in place. The turn already sits in the thread with earlier
        // answers above it, and rebuilding the list would scroll them away.
        const index = turns.value.findIndex((t) => t.id === progress.id);
        if (index >= 0) turns.value.splice(index, 1, progress);

        if (isFinished(progress)) {
            stopWatching();
            loadHistory();
            resolveLocal(turns.value.find((t) => t.id === progress.id));
        }
    }

    /**
     * The names and sizes of what this browser is holding, or null.
     *
     * Null on a deployment with the surface switched off, and null before the
     * vocabulary has said which it is — a device that will not be asked about
     * does not enumerate itself. Never values: buildLocalManifest() has no code
     * path that reads one, and the server drops anything else that arrives.
     */
    async function localManifest() {
        if (!vocabulary.value.local_state?.searches) return null;

        try {
            return await buildLocalManifest();
        } catch {
            // A browser that will not be counted is simply a browser that
            // cannot be asked about. Never a reason to lose the question.
            return null;
        }
    }

    function stopWatching() {
        if (pollTimer) {
            clearInterval(pollTimer);
            pollTimer = null;
        }

        if (echoChannel) {
            echoChannel.stopListening('.AskProgress');
            echoChannel = null;
        }

        const userId = auth.user?.id;
        if (userId && window.Echo) echo.leave(`ask.${userId}`);

        watchingId = null;
    }

    /**
     * Reopen a past conversation, with every turn of it.
     *
     * The thread is fetched rather than reconstructed from the history row,
     * which carries only a title and a count. A thread whose last turn never
     * finished is picked back up: reload the page mid-question and it comes
     * back still processing, and without this the pane would sit on a spinner
     * nothing is ever going to replace.
     */
    async function show(conversation) {
        const id = conversation?.conversation_id ?? conversation?.id;
        if (!id) return;

        stopWatching();
        errorMessage.value = '';

        try {
            const { data } = await apiClient.get(`ask/conversation/${id}`, { skipProgress: true });

            conversationId.value = id;
            turns.value = data.data ?? [];

            // Every local turn runs again against this browser as it is now,
            // which is the only honest way to reopen one.
            turns.value.forEach((turn) => resolveLocal(turn));

            const last = turns.value[turns.value.length - 1];
            if (last && !isFinished(last)) startWatching(last.id);
        } catch (error) {
            errorMessage.value = error.response?.data?.message || 'That conversation could not be opened.';
        }
    }

    /**
     * Put the conversation away and start a fresh one.
     *
     * Nothing is deleted — it stays in history. This only clears the pane and
     * cuts the thread, which is what somebody wants when the next question is
     * about something else entirely and they do not want last month's filters
     * carried into it.
     */
    function newChat() {
        stopWatching();
        turns.value = [];
        conversationId.value = null;
        errorMessage.value = '';
    }

    /**
     * Delete a conversation for good, every turn of it.
     *
     * Optimistic: the row leaves the list before the request returns, because
     * the alternative is a visible lag on a button whose whole purpose is
     * tidying up. A failure puts it back and says so.
     */
    async function remove(conversation) {
        const id = conversation?.conversation_id ?? conversation?.id;
        if (!id) return;

        const index = history.value.findIndex((h) => h.conversation_id === id);
        const snapshot = index >= 0 ? history.value[index] : null;

        if (index >= 0) history.value.splice(index, 1);
        if (conversationId.value === id) newChat();

        try {
            await apiClient.delete(`ask/conversation/${id}`, { skipProgress: true });
        } catch (error) {
            if (snapshot) history.value.splice(index, 0, snapshot);
            errorMessage.value = error.response?.data?.message || 'That conversation could not be deleted.';
        }
    }

    async function loadHistory() {
        try {
            const { data } = await apiClient.post('ask/list', { perPage: 15 }, { skipProgress: true });
            history.value = data.data.data ?? [];
        } catch {
            // History is a convenience; never block the panel on it.
        }
    }

    async function loadVocabulary() {
        try {
            const { data } = await apiClient.get('ask/vocabulary', { skipProgress: true });
            vocabulary.value = data.data;
        } catch {
            // Suggestions are optional; the input still works without them.
        }
    }

    // ---------------------------------------------------------------
    // Dictation
    // ---------------------------------------------------------------

    /**
     * Two engines, one microphone, and each doing what it is good at.
     *
     * The **Web Speech API** streams words as they are spoken. That is what
     * makes dictation feel like dictation, it is free, and it needs no round
     * trip. What it is not is accurate on the things this box is used for:
     * branch and product names, Urdu and English in one sentence, a shop's
     * worth of background noise.
     *
     * So the audio is also **recorded**, and when the speaker stops it is sent
     * to be transcribed properly — by a model that has been told it is hearing
     * a question about stock and given this deployment's own vocabulary as a
     * spelling hint. The accurate text then replaces the live text.
     *
     * Every part of this degrades on its own. No MediaRecorder, no permission,
     * transcription switched off on the server, or a failed call: whatever the
     * browser heard stays in the box, which is exactly what this feature did
     * before. Firefox has no SpeechRecognition at all, so the button is hidden
     * rather than shown broken.
     */
    const SpeechRecognition = typeof window !== 'undefined' ? (window.SpeechRecognition ?? window.webkitSpeechRecognition) : null;

    const speechSupported = Boolean(SpeechRecognition);
    const canRecord =
        typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined' && Boolean(navigator?.mediaDevices?.getUserMedia);

    const listening = ref(false);
    /** Between "stop talking" and the accurate transcript landing. */
    const transcribing = ref(false);

    let recognition = null;
    let recorder = null;
    let recordedChunks = [];
    let micStream = null;
    let recordingLimit = null;

    /** What was in the box before dictation started, so results extend it rather than replace it. */
    let dictationBase = '';
    /** Speech results already marked final, which interim results must not overwrite. */
    let finalText = '';
    /** Set while stopping, so the recogniser's own restart loop knows not to. */
    let stopping = false;
    /** The language dictation was started in, needed again when the recording is sent. */
    let dictationLocale = null;

    function transcriptionOffered() {
        return canRecord && vocabulary.value.dictation?.transcribes === true;
    }

    /** The first container this browser will actually record into. */
    function recordingMimeType() {
        const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];

        return candidates.find((type) => window.MediaRecorder.isTypeSupported?.(type)) ?? '';
    }

    async function startRecording() {
        if (!transcriptionOffered()) return;

        try {
            micStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    // The three that matter in a shop. Browsers that do not
                    // implement them ignore the constraint rather than failing.
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });

            const mimeType = recordingMimeType();
            recordedChunks = [];
            recorder = new window.MediaRecorder(micStream, mimeType ? { mimeType } : undefined);

            recorder.ondataavailable = (event) => {
                if (event.data?.size) recordedChunks.push(event.data);
            };

            recorder.onstop = () => {
                const blob = recordedChunks.length ? new Blob(recordedChunks, { type: recorder.mimeType || 'audio/webm' }) : null;
                recordedChunks = [];
                releaseMicrophone();
                if (blob) sendForTranscription(blob);
            };

            recorder.start();

            // A hard cap, matching the server's. A recorder left running
            // because somebody walked away should not produce an upload that
            // will only be refused.
            const seconds = Number(vocabulary.value.dictation?.max_seconds ?? 60);
            recordingLimit = setTimeout(() => stopDictation(), Math.max(5, seconds) * 1000);
        } catch {
            // Denied, or no microphone. Speech recognition asks for its own
            // permission and will report that itself; there is nothing to add.
            releaseMicrophone();
        }
    }

    function releaseMicrophone() {
        if (recordingLimit) {
            clearTimeout(recordingLimit);
            recordingLimit = null;
        }

        micStream?.getTracks().forEach((track) => track.stop());
        micStream = null;
        recorder = null;
    }

    /**
     * Send the recording and take its transcript over the live text.
     *
     * Only over the part that was dictated: whatever was typed before the
     * microphone opened is kept in front of it, so dictating into a
     * half-written question does not eat the half already there.
     */
    async function sendForTranscription(blob) {
        transcribing.value = true;

        try {
            const form = new FormData();
            form.append('audio', blob, 'dictation.webm');
            if (dictationLocale) form.append('locale', dictationLocale);

            const { data } = await apiClient.post('ask/transcribe', form, { offlinePolicy: 'mutation', skipProgress: true });

            const transcript = (data?.transcript ?? '').trim();

            if (transcript) question.value = (dictationBase ? dictationBase + ' ' : '') + transcript;
        } catch {
            // Keep what the browser heard. A failed upgrade is not a failure —
            // it is the feature as it shipped before there was an upgrade.
        } finally {
            transcribing.value = false;
        }
    }

    async function startDictation(locale = 'en-US') {
        if (!speechSupported || listening.value) return;

        stopping = false;
        dictationLocale = locale;
        dictationBase = question.value.trim();
        finalText = '';

        // Before the recogniser, so the two are listening to the same sentence.
        await startRecording();

        recognition = new SpeechRecognition();
        recognition.lang = locale;
        // Chromium ends a non-continuous session at the first pause, which
        // truncates any question with a comma in it. Continuous plus the
        // restart in onend is what makes a whole spoken sentence survive.
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event) => {
            let interim = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                const text = result[0]?.transcript ?? '';

                // A final result is committed; only interim text is redrawn, or
                // a long sentence loses everything said before the last pause.
                if (result.isFinal) finalText += text;
                else interim += text;
            }

            const spoken = (finalText + interim).trim();
            question.value = (dictationBase ? dictationBase + ' ' : '') + spoken;
        };

        recognition.onerror = (event) => {
            // 'aborted' and 'no-speech' are ordinary — somebody stopped, or
            // paused for longer than the engine waits. Only a real fault is
            // worth a message, and only 'not-allowed' ends the session.
            if (event.error === 'not-allowed') {
                errorMessage.value = 'Microphone access was blocked. Allow it in your browser to dictate.';
                stopDictation();
                return;
            }

            if (!['aborted', 'no-speech'].includes(event.error)) {
                errorMessage.value = 'Dictation stopped unexpectedly.';
            }
        };

        recognition.onend = () => {
            // The engine stops itself after a few seconds of silence. While the
            // speaker has not pressed stop, that is a pause in a sentence and
            // not the end of one, so pick it straight back up.
            if (!stopping && listening.value) {
                try {
                    recognition.start();
                    return;
                } catch {
                    // Already restarting, or gone. Fall through and finish.
                }
            }

            listening.value = false;
            recognition = null;
        };

        try {
            recognition.start();
            listening.value = true;
            errorMessage.value = '';
        } catch {
            listening.value = false;
            recognition = null;
            stopRecording();
        }
    }

    function stopRecording() {
        if (recorder && recorder.state !== 'inactive') {
            try {
                // onstop does the rest, including releasing the microphone.
                recorder.stop();
                return;
            } catch {
                // Already stopping.
            }
        }

        releaseMicrophone();
    }

    function stopDictation() {
        stopping = true;
        listening.value = false;

        if (recognition) {
            try {
                recognition.stop();
            } catch {
                // Already stopping.
            }
        }

        stopRecording();
    }

    /** Returns startDictation's promise, because opening the microphone is a round trip. */
    function toggleDictation(locale) {
        return listening.value ? stopDictation() : startDictation(locale);
    }

    onBeforeUnmount(() => {
        stopWatching();
        stopDictation();
    });

    /*
     * Returned reactive, not as a bare object of refs.
     *
     * Every consumer holds this instance behind a name — `ask.turns`,
     * `ask.pending` — either as a local binding or as a prop. Vue unwraps refs
     * in a template only at the top level of the setup state, never one property
     * deep, so a plain object here hands the template the Ref objects
     * themselves. `v-if="ask.pending"` is then true forever: the panel opens on
     * a blank card that says it is working through your data and never stops,
     * and the error line renders permanently with nothing in it. reactive()
     * unwraps on read, so the templates mean what they say.
     */
    return reactive({
        // state
        question,
        busy,
        errorMessage,
        conversationId,
        turns,
        latest,
        started,
        history,
        vocabulary,
        maxLength,
        pending,
        suggestions,
        // actions
        ask,
        askThis,
        show,
        newChat,
        remove,
        loadHistory,
        loadVocabulary,
        stopWatching,
        // dictation
        speechSupported,
        listening,
        transcribing,
        toggleDictation,
        stopDictation,
        // formatting
        chipsFor,
        resultOf,
        humanise,
        localStoreLabel,
        rowLabel,
        cell,
        formatValue,
        shortDate,
    });
}
